import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { useTheme } from "next-themes";
import * as React from "react";

export default function GridBeams({ className = "" }: { className?: string }) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const isSmall = useMediaQuery("(max-width: 640px)");

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="420"
      height="260"
      fill="none"
      viewBox="0 0 216 144"
      className={`${className}`}
    >
      {/* ====== GLOBAL DEFS (single, consolidated) ====== */}
      <defs>
        {/* grid mask gradient used by the grid (kept from your original file) */}
        <linearGradient
          id="geo-grid-mask-grad"
          x1="0"
          x2="0"
          y1="0"
          y2="86%"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.535" stopColor="currentColor" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>

        {/* clip to the image rect (beams will be visible only inside this) */}

        {/* per-beam gradients (visible on both light/dark) */}
        <linearGradient id="beam-grad-1" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={isDark ? "#FFFFFF" : "#000000"} stopOpacity="0" />
          <stop offset="0%" stopColor={isDark ? "#FFFFFF" : "#000000"} stopOpacity="0.95" />
          <stop offset="100%" stopColor={isDark ? "#FFFFFF" : "#000000"} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="beam-grad-2" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={isDark ? "#FFFFFF" : "#000000"} stopOpacity="0" />
          <stop offset="30%" stopColor={isDark ? "#FFFFFF" : "#000000"} stopOpacity="0.95" />
          <stop offset="100%" stopColor={isDark ? "#FFFFFF" : "#000000"} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="beam-grad-3" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={isDark ? "#FFFFFF" : "#000000"} stopOpacity="0" />
          <stop offset="0%" stopColor={isDark ? "#FFFFFF" : "#000000"} stopOpacity="0.95" />
          <stop offset="100%" stopColor={isDark ? "#FFFFFF" : "#000000"} stopOpacity="0" />
        </linearGradient>
        <linearGradient id="beam-grad-4" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={isDark ? "#FFFFFF" : "#000000"} stopOpacity="0" />
          <stop offset="20%" stopColor={isDark ? "#FFFFFF" : "#000000"} stopOpacity="0.95" />
          <stop offset="100%" stopColor={isDark ? "#FFFFFF" : "#000000"} stopOpacity="0" />
        </linearGradient>
      </defs>

      <mask
        id="geo-grid-mask"
        width="100%"
        height="100%"
        x="0"
        y="0"
        fill="none"
        maskUnits="userSpaceOnUse"
        style={{ maskType: "alpha" }}
      >
        <path fill='url("#geo-grid-mask-grad")' d="M0 0h216v144H0z"></path>
      </mask>
      <g mask="url(#geo-grid-mask)">
        <path stroke="currentColor" d="M.5.5h215v125H.5z"></path>
        <mask id="geoc" fill="#FFF">
          <path d="M0 0h18v18H0z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M18 18v1h1v-1zM17 0v18h2V0zm1 17H0v2h18z"
          mask="url(#geoc)"
        ></path>
        <mask id="geod" fill="#FFF">
          <path d="M18 0h18v18H18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M36 18v1h1v-1zM35 0v18h2V0zm1 17H18v2h18z"
          mask="url(#geod)"
        ></path>
        <mask id="geoe" fill="#FFF">
          <path d="M36 0h18v18H36z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M54 18v1h1v-1zM53 0v18h2V0zm1 17H36v2h18z"
          mask="url(#geoe)"
        ></path>
        <mask id="geof" fill="#FFF">
          <path d="M54 0h18v18H54z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M72 18v1h1v-1zM71 0v18h2V0zm1 17H54v2h18z"
          mask="url(#geof)"
        ></path>
        <mask id="geog" fill="#FFF">
          <path d="M72 0h18v18H72z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M90 18v1h1v-1zM89 0v18h2V0zm1 17H72v2h18z"
          mask="url(#geog)"
        ></path>
        <mask id="geoh" fill="#FFF">
          <path d="M90 0h18v18H90z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M108 18v1h1v-1zm-1-18v18h2V0zm1 17H90v2h18z"
          mask="url(#geoh)"
        ></path>
        <mask id="geoi" fill="#FFF">
          <path d="M108 0h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M126 18v1h1v-1zm-1-18v18h2V0zm1 17h-18v2h18z"
          mask="url(#geoi)"
        ></path>
        <mask id="geoj" fill="#FFF">
          <path d="M126 0h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M144 18v1h1v-1zm-1-18v18h2V0zm1 17h-18v2h18z"
          mask="url(#geoj)"
        ></path>
        <mask id="geok" fill="#FFF">
          <path d="M144 0h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M162 18v1h1v-1zm-1-18v18h2V0zm1 17h-18v2h18z"
          mask="url(#geok)"
        ></path>
        <mask id="geol" fill="#FFF">
          <path d="M162 0h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M180 18v1h1v-1zm-1-18v18h2V0zm1 17h-18v2h18z"
          mask="url(#geol)"
        ></path>
        <mask id="geom" fill="#FFF">
          <path d="M180 0h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M198 18v1h1v-1zm-1-18v18h2V0zm1 17h-18v2h18z"
          mask="url(#geom)"
        ></path>
        <mask id="geon" fill="#FFF">
          <path d="M198 0h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M216 18v1h1v-1zm-1-18v18h2V0zm1 17h-18v2h18z"
          mask="url(#geon)"
        ></path>
        <mask id="geoo" fill="#FFF">
          <path d="M0 18h18v18H0z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M18 36v1h1v-1zm-1-18v18h2V18zm1 17H0v2h18z"
          mask="url(#geoo)"
        ></path>
        <mask id="geop" fill="#FFF">
          <path d="M18 18h18v18H18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M36 36v1h1v-1zm-1-18v18h2V18zm1 17H18v2h18z"
          mask="url(#geop)"
        ></path>
        <mask id="geoq" fill="#FFF">
          <path d="M36 18h18v18H36z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M54 36v1h1v-1zm-1-18v18h2V18zm1 17H36v2h18z"
          mask="url(#geoq)"
        ></path>
        <mask id="geor" fill="#FFF">
          <path d="M54 18h18v18H54z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M72 36v1h1v-1zm-1-18v18h2V18zm1 17H54v2h18z"
          mask="url(#geor)"
        ></path>
        <mask id="geos" fill="#FFF">
          <path d="M72 18h18v18H72z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M90 36v1h1v-1zm-1-18v18h2V18zm1 17H72v2h18z"
          mask="url(#geos)"
        ></path>
        <mask id="geot" fill="#FFF">
          <path d="M90 18h18v18H90z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M108 36v1h1v-1zm-1-18v18h2V18zm1 17H90v2h18z"
          mask="url(#geot)"
        ></path>
        <mask id="geou" fill="#FFF">
          <path d="M108 18h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M126 36v1h1v-1zm-1-18v18h2V18zm1 17h-18v2h18z"
          mask="url(#geou)"
        ></path>
        <mask id="geov" fill="#FFF">
          <path d="M126 18h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M144 36v1h1v-1zm-1-18v18h2V18zm1 17h-18v2h18z"
          mask="url(#geov)"
        ></path>
        <mask id="geow" fill="#FFF">
          <path d="M144 18h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M162 36v1h1v-1zm-1-18v18h2V18zm1 17h-18v2h18z"
          mask="url(#geow)"
        ></path>
        <mask id="geox" fill="#FFF">
          <path d="M162 18h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M180 36v1h1v-1zm-1-18v18h2V18zm1 17h-18v2h18z"
          mask="url(#geox)"
        ></path>
        <mask id="geoy" fill="#FFF">
          <path d="M180 18h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M198 36v1h1v-1zm-1-18v18h2V18zm1 17h-18v2h18z"
          mask="url(#geoy)"
        ></path>
        <mask id="geoz" fill="#FFF">
          <path d="M198 18h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M216 36v1h1v-1zm-1-18v18h2V18zm1 17h-18v2h18z"
          mask="url(#geoz)"
        ></path>
        <mask id="geoA" fill="#FFF">
          <path d="M0 36h18v18H0z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M18 54v1h1v-1zm-1-18v18h2V36zm1 17H0v2h18z"
          mask="url(#geoA)"
        ></path>
        <mask id="geoB" fill="#FFF">
          <path d="M18 36h18v18H18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M36 54v1h1v-1zm-1-18v18h2V36zm1 17H18v2h18z"
          mask="url(#geoB)"
        ></path>
        <mask id="geoC" fill="#FFF">
          <path d="M36 36h18v18H36z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M54 54v1h1v-1zm-1-18v18h2V36zm1 17H36v2h18z"
          mask="url(#geoC)"
        ></path>
        <mask id="geoD" fill="#FFF">
          <path d="M54 36h18v18H54z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M72 54v1h1v-1zm-1-18v18h2V36zm1 17H54v2h18z"
          mask="url(#geoD)"
        ></path>
        <mask id="geoE" fill="#FFF">
          <path d="M72 36h18v18H72z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M90 54v1h1v-1zm-1-18v18h2V36zm1 17H72v2h18z"
          mask="url(#geoE)"
        ></path>
        <mask id="geoF" fill="#FFF">
          <path d="M90 36h18v18H90z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M108 54v1h1v-1zm-1-18v18h2V36zm1 17H90v2h18z"
          mask="url(#geoF)"
        ></path>
        <mask id="geoG" fill="#FFF">
          <path d="M108 36h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M126 54v1h1v-1zm-1-18v18h2V36zm1 17h-18v2h18z"
          mask="url(#geoG)"
        ></path>
        <mask id="geoH" fill="#FFF">
          <path d="M126 36h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M144 54v1h1v-1zm-1-18v18h2V36zm1 17h-18v2h18z"
          mask="url(#geoH)"
        ></path>
        <mask id="geoI" fill="#FFF">
          <path d="M144 36h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M162 54v1h1v-1zm-1-18v18h2V36zm1 17h-18v2h18z"
          mask="url(#geoI)"
        ></path>
        <mask id="geoJ" fill="#FFF">
          <path d="M162 36h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M180 54v1h1v-1zm-1-18v18h2V36zm1 17h-18v2h18z"
          mask="url(#geoJ)"
        ></path>
        <mask id="geoK" fill="#FFF">
          <path d="M180 36h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M198 54v1h1v-1zm-1-18v18h2V36zm1 17h-18v2h18z"
          mask="url(#geoK)"
        ></path>
        <mask id="geoL" fill="#FFF">
          <path d="M198 36h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M216 54v1h1v-1zm-1-18v18h2V36zm1 17h-18v2h18z"
          mask="url(#geoL)"
        ></path>
        <mask id="geoM" fill="#FFF">
          <path d="M0 54h18v18H0z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M18 72v1h1v-1zm-1-18v18h2V54zm1 17H0v2h18z"
          mask="url(#geoM)"
        ></path>
        <mask id="geoN" fill="#FFF">
          <path d="M18 54h18v18H18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M36 72v1h1v-1zm-1-18v18h2V54zm1 17H18v2h18z"
          mask="url(#geoN)"
        ></path>
        <mask id="geoO" fill="#FFF">
          <path d="M36 54h18v18H36z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M54 72v1h1v-1zm-1-18v18h2V54zm1 17H36v2h18z"
          mask="url(#geoO)"
        ></path>
        <mask id="geoP" fill="#FFF">
          <path d="M54 54h18v18H54z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M72 72v1h1v-1zm-1-18v18h2V54zm1 17H54v2h18z"
          mask="url(#geoP)"
        ></path>
        <mask id="geoQ" fill="#FFF">
          <path d="M72 54h18v18H72z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M90 72v1h1v-1zm-1-18v18h2V54zm1 17H72v2h18z"
          mask="url(#geoQ)"
        ></path>
        <mask id="geoR" fill="#FFF">
          <path d="M90 54h18v18H90z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M108 72v1h1v-1zm-1-18v18h2V54zm1 17H90v2h18z"
          mask="url(#geoR)"
        ></path>
        <mask id="geoS" fill="#FFF">
          <path d="M108 54h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M126 72v1h1v-1zm-1-18v18h2V54zm1 17h-18v2h18z"
          mask="url(#geoS)"
        ></path>
        <mask id="geoT" fill="#FFF">
          <path d="M126 54h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M144 72v1h1v-1zm-1-18v18h2V54zm1 17h-18v2h18z"
          mask="url(#geoT)"
        ></path>
        <mask id="geoU" fill="#FFF">
          <path d="M144 54h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M162 72v1h1v-1zm-1-18v18h2V54zm1 17h-18v2h18z"
          mask="url(#geoU)"
        ></path>
        <mask id="geoV" fill="#FFF">
          <path d="M162 54h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M180 72v1h1v-1zm-1-18v18h2V54zm1 17h-18v2h18z"
          mask="url(#geoV)"
        ></path>
        <mask id="geoW" fill="#FFF">
          <path d="M180 54h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M198 72v1h1v-1zm-1-18v18h2V54zm1 17h-18v2h18z"
          mask="url(#geoW)"
        ></path>
        <mask id="geoX" fill="#FFF">
          <path d="M198 54h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M216 72v1h1v-1zm-1-18v18h2V54zm1 17h-18v2h18z"
          mask="url(#geoX)"
        ></path>
        <mask id="geoY" fill="#FFF">
          <path d="M0 72h18v18H0z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M18 90v1h1v-1zm-1-18v18h2V72zm1 17H0v2h18z"
          mask="url(#geoY)"
        ></path>
        <mask id="geoZ" fill="#FFF">
          <path d="M18 72h18v18H18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M36 90v1h1v-1zm-1-18v18h2V72zm1 17H18v2h18z"
          mask="url(#geoZ)"
        ></path>
        <mask id="geoaa" fill="#FFF">
          <path d="M36 72h18v18H36z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M54 90v1h1v-1zm-1-18v18h2V72zm1 17H36v2h18z"
          mask="url(#geoaa)"
        ></path>
        <mask id="geoab" fill="#FFF">
          <path d="M54 72h18v18H54z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M72 90v1h1v-1zm-1-18v18h2V72zm1 17H54v2h18z"
          mask="url(#geoab)"
        ></path>
        <mask id="geoac" fill="#FFF">
          <path d="M72 72h18v18H72z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M90 90v1h1v-1zm-1-18v18h2V72zm1 17H72v2h18z"
          mask="url(#geoac)"
        ></path>
        <mask id="geoad" fill="#FFF">
          <path d="M90 72h18v18H90z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M108 90v1h1v-1zm-1-18v18h2V72zm1 17H90v2h18z"
          mask="url(#geoad)"
        ></path>
        <mask id="geoae" fill="#FFF">
          <path d="M108 72h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M126 90v1h1v-1zm-1-18v18h2V72zm1 17h-18v2h18z"
          mask="url(#geoae)"
        ></path>
        <mask id="geoaf" fill="#FFF">
          <path d="M126 72h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M144 90v1h1v-1zm-1-18v18h2V72zm1 17h-18v2h18z"
          mask="url(#geoaf)"
        ></path>
        <mask id="geoag" fill="#FFF">
          <path d="M144 72h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M162 90v1h1v-1zm-1-18v18h2V72zm1 17h-18v2h18z"
          mask="url(#geoag)"
        ></path>
        <mask id="geoah" fill="#FFF">
          <path d="M162 72h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M180 90v1h1v-1zm-1-18v18h2V72zm1 17h-18v2h18z"
          mask="url(#geoah)"
        ></path>
        <mask id="geoai" fill="#FFF">
          <path d="M180 72h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M198 90v1h1v-1zm-1-18v18h2V72zm1 17h-18v2h18z"
          mask="url(#geoai)"
        ></path>
        <mask id="geoaj" fill="#FFF">
          <path d="M198 72h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M216 90v1h1v-1zm-1-18v18h2V72zm1 17h-18v2h18z"
          mask="url(#geoaj)"
        ></path>
        <mask id="geoak" fill="#FFF">
          <path d="M0 90h18v18H0z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M18 108v1h1v-1zm-1-18v18h2V90zm1 17H0v2h18z" /* 3beam */
          mask="url(#geoak)"
        ></path>
        <mask id="geoal" fill="#FFF">
          <path d="M18 90h18v18H18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M36 108v1h1v-1zm-1-18v18h2V90zm1 17H18v2h18z"
          mask="url(#geoal)"
        ></path>
        <mask id="geoam" fill="#FFF">
          <path d="M36 90h18v18H36z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M54 108v1h1v-1zm-1-18v18h2V90zm1 17H36v2h18z"
          mask="url(#geoam)"
        ></path>
        <mask id="geoan" fill="#FFF">
          <path d="M54 90h18v18H54z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M72 108v1h1v-1zm-1-18v18h2V90zm1 17H54v2h18z"
          mask="url(#geoan)"
        ></path>
        <mask id="geoao" fill="#FFF">
          <path d="M72 90h18v18H72z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M90 108v1h1v-1zm-1-18v18h2V90zm1 17H72v2h18z"
          mask="url(#geoao)"
        ></path>
        <mask id="geoap" fill="#FFF">
          <path d="M90 90h18v18H90z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M108 108v1h1v-1zm-1-18v18h2V90zm1 17H90v2h18z"
          mask="url(#geoap)"
        ></path>
        <mask id="geoaq" fill="#FFF">
          <path d="M108 90h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M126 108v1h1v-1zm-1-18v18h2V90zm1 17h-18v2h18z"
          mask="url(#geoaq)"
        ></path>
        <mask id="geoar" fill="#FFF">
          <path d="M126 90h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M144 108v1h1v-1zm-1-18v18h2V90zm1 17h-18v2h18z"
          mask="url(#geoar)"
        ></path>
        <mask id="geoas" fill="#FFF">
          <path d="M144 90h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M162 108v1h1v-1zm-1-18v18h2V90zm1 17h-18v2h18z"
          mask="url(#geoas)"
        ></path>
        <mask id="geoat" fill="#FFF">
          <path d="M162 90h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M180 108v1h1v-1zm-1-18v18h2V90zm1 17h-18v2h18z"
          mask="url(#geoat)"
        ></path>
        <mask id="geoau" fill="#FFF">
          <path d="M180 90h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M198 108v1h1v-1zm-1-18v18h2V90zm1 17h-18v2h18z"
          mask="url(#geoau)"
        ></path>
        <mask id="geoav" fill="#FFF">
          <path d="M198 90h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M216 108v1h1v-1zm-1-18v18h2V90zm1 17h-18v2h18z"
          mask="url(#geoav)"
        ></path>
        <mask id="geoaw" fill="#FFF">
          <path d="M0 108h18v18H0z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M18 126v1h1v-1zm-1-18v18h2v-18zm1 17H0v2h18z"
          mask="url(#geoaw)"
        ></path>
        <mask id="geoax" fill="#FFF">
          <path d="M18 108h18v18H18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M36 126v1h1v-1zm-1-18v18h2v-18zm1 17H18v2h18z"
          mask="url(#geoax)"
        ></path>
        <mask id="geoay" fill="#FFF">
          <path d="M36 108h18v18H36z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M54 126v1h1v-1zm-1-18v18h2v-18zm1 17H36v2h18z"
          mask="url(#geoay)"
        ></path>
        <mask id="geoaz" fill="#FFF">
          <path d="M54 108h18v18H54z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M72 126v1h1v-1zm-1-18v18h2v-18zm1 17H54v2h18z"
          mask="url(#geoaz)"
        ></path>
        <mask id="geoaA" fill="#FFF">
          <path d="M72 108h18v18H72z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M90 126v1h1v-1zm-1-18v18h2v-18zm1 17H72v2h18z"
          mask="url(#geoaA)"
        ></path>
        <mask id="geoaB" fill="#FFF">
          <path d="M90 108h18v18H90z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M108 126v1h1v-1zm-1-18v18h2v-18zm1 17H90v2h18z"
          mask="url(#geoaB)"
        ></path>
        <mask id="geoaC" fill="#FFF">
          <path d="M108 108h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M126 126v1h1v-1zm-1-18v18h2v-18zm1 17h-18v2h18z"
          mask="url(#geoaC)"
        ></path>
        <mask id="geoaD" fill="#FFF">
          <path d="M126 108h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M144 126v1h1v-1zm-1-18v18h2v-18zm1 17h-18v2h18z"
          mask="url(#geoaD)"
        ></path>
        <mask id="geoaE" fill="#FFF">
          <path d="M144 108h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M162 126v1h1v-1zm-1-18v18h2v-18zm1 17h-18v2h18z"
          mask="url(#geoaE)"
        ></path>
        <mask id="geoaF" fill="#FFF">
          <path d="M162 108h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M180 126v1h1v-1zm-1-18v18h2v-18zm1 17h-18v2h18z"
          mask="url(#geoaF)"
        ></path>
        <mask id="geoaG" fill="#FFF">
          <path d="M180 108h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M198 126v1h1v-1zm-1-18v18h2v-18zm1 17h-18v2h18z"
          mask="url(#geoaG)"
        ></path>
        <mask id="geoaH" fill="#FFF">
          <path d="M198 108h18v18h-18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M216 126v1h1v-1zm-1-18v18h2v-18zm1 17h-18v2h18z"
          mask="url(#geoaH)"
        ></path>
        <mask id="geoaI" fill="#FFF">
          <path d="M18 54h72v72H18z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M90 126v1h1v-1zm-1-72v72h2V54zm1 71H18v2h72z"
          mask="url(#geoaI)"
        ></path>
        <mask id="geoaJ" fill="#FFF">
          <path d="M126 54h72v72h-72z"></path>
        </mask>
        <path
          fill="currentColor"
          d="M198 126v1h1v-1zm-1-72v72h2V54zm1 71h-72v2h72z"
          mask="url(#geoaJ)"
        ></path>
      </g>

      {/* Image */}
      <rect
        width="72"
        height="72"
        x="125.5"
        y="17.5"
        stroke="currentColor"
        rx="1.12"
        fill="black"
        className="fill-white dark:fill-black"
      />
      <rect
        width="65"
        height="65"
        x="129"
        y="21"
        rx="1.62"
        className="fill-[#EAEAEA] dark:fill-[#2E2E2E]"
      />

      <clipPath id="clip-rect">
        <rect
          width="65"
          height="65"
          x="129"
          y="31"
          rx="1.62"
          transform="translate(-74 63.5) scale(0.714)"
        />
      </clipPath>

      <g
        transform="translate(103.6 -98.7) scale(1.4)"
        clipPath="url(#clip-rect)"
        className="fill-white dark:fill-[#0A0A0A]"
      >
        <path d="m31.43 121.535-2.122-1.832a2.93 2.93 0 0 0-4.048.208l-12.467 13.234c-1.761 1.87-.436 4.939 2.133 4.939H74.7c2.553 0 3.885-3.038 2.154-4.915l-24.316-26.381a2.93 2.93 0 0 0-4.357.054l-12.634 14.407a2.93 2.93 0 0 1-4.118.286"></path>
        <circle cx="27.766" cy="104.394" r="5.86"></circle>
      </g>

      <linearGradient
        id="geo-grid-mask-grad"
        x1="0"
        x2="0"
        y1="0"
        y2="86%"
        fill="none"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.535" stopColor="#D9D9D9"></stop>
        <stop offset="1" stopColor="#D9D9D9" stopOpacity="0"></stop>
      </linearGradient>

      {/* Bottom Content */}
      <rect
        width="89.28"
        height="35.16"
        x="46.5"
        y="-58.5"
        stroke="currentColor"
        rx="1.12"
        transform="matrix(-1 0 0 1 153.562 112.32)"
        className="fill-white dark:fill-black"
      ></rect>
      <rect
        width="80.92"
        height="3.24"
        x="21.828"
        y="69.07"
        stroke="currentColor"
        rx="1.12"
        className="fill-white dark:fill-[#0A0A0A]"
      ></rect>
      <rect
        width="38.44"
        height="5.48"
        x="21.828"
        y="59.07"
        stroke="currentColor"
        rx="1.12"
        className="fill-white dark:fill-[#0A0A0A]"
      ></rect>
      <rect
        width="65.92"
        height="3.24"
        x="21.828"
        y="75.8"
        stroke="currentColor"
        rx="1.12"
        className="fill-white dark:fill-[#0A0A0A]"
      ></rect>
      <rect
        width="80.92"
        height="3.24"
        x="21.828"
        y="82.07"
        stroke="currentColor"
        rx="1.12"
        className="fill-white dark:fill-[#0A0A0A]"
      ></rect>

      {/* Top Content */}
      <rect
        width="89.28"
        height="17.16"
        x="46.5"
        y="-94.5"
        stroke="currentColor"
        rx="1.12"
        transform="matrix(-1 0 0 1 153.562 112.32)"
        className="fill-white dark:fill-black"
      ></rect>
      <rect
        width="23.44"
        height="6.48"
        x="21.828"
        y="23.07"
        stroke="currentColor"
        rx="1.12"
        className="fill-white dark:fill-[#0A0A0A]"
      ></rect>
      <rect
        width="23.44"
        height="6.48"
        x="50.4"
        y="23.07"
        stroke="currentColor"
        rx="1.12"
        className="fill-white dark:fill-[#0A0A0A]"
      ></rect>
      <rect
        width="23.44"
        height="6.48"
        x="78.828"
        y="23.07"
        stroke="currentColor"
        rx="1.12"
        className="fill-white dark:fill-[#0A0A0A]"
      ></rect>

      {/* Animated beams */}

      {/* ===== BEAMS GROUP (clipped to image rect) ===== */}
      <g clipPath="url(#grid-clip)">
        {/* IMPORTANT: replace the d values below with the real paths you want.
            pathLength="100" normalizes the path for dash animation. */}
        <path
          className="beam beam-1"
          d="M0 0 L35 0 L35 17 L90 17"
          stroke="url(#beam-grad-1)"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          pathLength={isSmall ? "50" : "30"}
        />
        <path
          className="beam beam-2"
          d="M220 108 L160 108 L160 89 L160 89 L125 89 L125 60 L120 60"
          stroke="url(#beam-grad-2)"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          pathLength={isSmall ? "50" : "30"}
        />
        
        <path
          className="beam beam-3"
          d="M0 71 L18 71 L18 89 L18 89 L80 88"
          stroke="url(#beam-grad-3)"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          pathLength={isSmall ? "50" : "30"}
        />
         <path
          className="beam beam-4"
          d="M220 0 L180 0 L180 17 L140 17"
          stroke="url(#beam-grad-4)"
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
          pathLength={isSmall ? "50" : "30"}
        /> 
      </g>


      <defs>
        <linearGradient id="beam-gradient" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="cyan" stopOpacity="1" />
          <stop offset="100%" stopColor="cyan" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* ===== INLINE CSS (sequences the four beams; total cycle = 4s) ===== */}
      <style>{`
/* beam visual */
.beam {
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
  stroke-dasharray: 100;       /* normalized length */
  stroke-dashoffset: 0;      /* hidden initially */
  opacity: 0;                  /* hidden initially */
  mix-blend-mode: screen;      /* nice glow blend */
  filter: drop-shadow(0 0 8px rgba(0,0,0,0.12));
  /* total cycle is 4s, each beam's window is 1s within that cycle */
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

/* attach each beam to its own keyframes */
.beam-1 { animation-name: beam1; animation-duration: 5s; }
.beam-2 { animation-name: beam2; animation-duration: 5s; }
.beam-3 { animation-name: beam3; animation-duration: 5s; }
.beam-4 { animation-name: beam4; animation-duration: 5s; }

/* Beam 1 active 0% -> 25% of the cycle */
@keyframes beam1 {
  0%    { stroke-dashoffset: 100; opacity: 0; }
  2%    { opacity: 1; }                     /* fade in quickly */
  24.9% { stroke-dashoffset: 0; opacity: 0; }/* fully drawn at ~25% */
  25%   { opacity: 0; stroke-dashoffset: 0; }/* hide at the boundary */
  100%  { opacity: 0; stroke-dashoffset: 0; }
}

/* Beam 2 active 25% -> 50% */
@keyframes beam2 {
  0%    { stroke-dashoffset: 0; opacity: 0; }
  24.9% { stroke-dashoffset: 0; opacity: 0; } /* remain hidden until window */
  25%   { stroke-dashoffset: 100; opacity: 0; }/* prepare: hidden at offset 100 */
  27%   { opacity: 1; }                        /* quick fade in */
  49.9% { stroke-dashoffset: 0; opacity: 0.2; } /* drawn at 50% */
  50%   { opacity: 0; stroke-dashoffset: 0; }
  100%  { opacity: 0; stroke-dashoffset: 0; }
}

/* Beam 3 active 50% -> 75% */
@keyframes beam3 {
  0%    { stroke-dashoffset: 0; opacity: 0; }
  49.9% { stroke-dashoffset: 0; opacity: 0; }
  50%   { stroke-dashoffset: 100; opacity: 0; }
  52%   { opacity: 1; }
  74.9% { stroke-dashoffset: 0; opacity: 0.1; }
  75%   { opacity: 0; stroke-dashoffset: 0; }
  100%  { opacity: 0; stroke-dashoffset: 0; }
}

/* Beam 4 active 75% -> 100% */
@keyframes beam4 {
  0%    { stroke-dashoffset: 0; opacity: 0; }
  74.9% { stroke-dashoffset: 0; opacity: 0; }
  75%   { stroke-dashoffset: 100; opacity: 0; }
  77%   { opacity: 1; }
  99.9% { stroke-dashoffset: 0; opacity: 0.1; }
  100%  { opacity: 0; stroke-dashoffset: 0; } /* cycle resets to beam-1 */
}

      `}</style>
    </svg>
  );
}
